import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AutoComplete, Option } from "@/components/ui/autocomplete";
import { PurchaseOrder } from '@/gen/purchase_order/common_pb';
import { Supplier } from '@/gen/suppliers/common_pb';
import db from '@/db';
import AddSupplier from '@/routes/(pharma)/_layout/suppliers/-components/add-supplier';

type FormState = {
  invoice_date: Date | undefined;
  delivery_date: Date | undefined;
  invoice_number: string;
  po_id: string;
  payment_mode: string;
  payment_type: string;
  supplier_id: string;
};

type ChequeDetails = {
  chequeNumber: string;
  chequeDate: Date | undefined;
  bankName: string;
};

interface PurchaseInvoiceDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSubmit: (form: FormState, chequeDetails?: ChequeDetails) => void;
  suppliers: Supplier[];
  purchaseOrders: PurchaseOrder[];
  instance_id?: number; // Optional instance ID for tracking
}


const PurchaseInvoiceDialog: React.FC<PurchaseInvoiceDialogProps> = ({ open, setOpen, onSubmit, suppliers, purchaseOrders, instance_id = undefined }) => {
  const initialForm: FormState = {
    invoice_date: undefined,
    delivery_date: undefined,
    invoice_number: '',
    po_id: '',
    payment_mode: '',
    payment_type: '',
    supplier_id: '',
  };
  
  const initialChequeDetails: ChequeDetails = {
    chequeNumber: '',
    chequeDate: undefined,
    bankName: '',
  };
  const [form, setForm] = useState<FormState>(initialForm);
  const [chequeDetails, setChequeDetails] = useState<ChequeDetails>(initialChequeDetails);
  const [supplierInput, setSupplierInput] = useState('');
  const [supplierValue, setSupplierValue] = useState<Option | undefined>(undefined);

  // Popover open states for each date picker
  const [invoiceDateOpen, setInvoiceDateOpen] = useState(false);
  const [deliveryDateOpen, setDeliveryDateOpen] = useState(false);
  const [chequeDateOpen, setChequeDateOpen] = useState(false);

  const [allowSupplierToBeChanged, setAllowSupplierToBeChanged] = useState(true);

  const filteredSupplierData = useMemo(() => {
      return suppliers.filter(s => {
          if (form.payment_type === "INSTANT") {
              return true;
          }
          if (form.payment_type === "CREDIT") {
              return s.creditTerms?.isCreditAllowed
          }
          return true;
      }).map((s) => {
          return {
              value: s.id,
              label: s.name,
              is_credit_allowed: s.creditTerms?.isCreditAllowed.toString()!,
              supplierState: s.metadata?.state || "",
              isSezSupplier: s.metadata?.isSez.toString()!
          }
      })
  }, [form.payment_type])

  useEffect(() => {
    if (instance_id) {
      // Fetch existing invoice data if instance_id is provided
      db.create_pi_info.where('instance_id').equals(instance_id).first().then(existingInvoice => {
        if (existingInvoice) {
          setForm({
            ...form,
            po_id: existingInvoice.po_id || '',
            supplier_id: existingInvoice.supplier_id || '',
          })
          setAllowSupplierToBeChanged(existingInvoice.po_id !== "" ? false : true);
        }
      })
    }
  }, [instance_id])

  useEffect(() => {
      if (form.supplier_id) {
          const local_supplier = suppliers.find(s => s.id === form.supplier_id);
          if (local_supplier) {
              setSupplierValue({
                  label: local_supplier.name,
                  value: form.supplier_id,
                  is_credit_allowed: local_supplier.creditTerms?.isCreditAllowed.toString()!,
                  supplierState: local_supplier.metadata?.state || "",
                  isSezSupplier: local_supplier.metadata?.isSez.toString()!
              });
              setSupplierInput(local_supplier.name);
          }
      }
  }, [form.supplier_id, form.payment_type])

  const handleChange = (field: keyof FormState, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    onSubmit(form, form.payment_mode === 'CHEQUE' ? chequeDetails : undefined);
    setOpen(false);
  };

  const paymentModes = [
    { value: 'CASH', label: 'Cash' },
    { value: 'CHEQUE', label: 'Cheque' },
    { value: 'CREDIT_CARD', label: 'Credit Card' },
    { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  ];
  const paymentTypes = [
    { value: 'CREDIT', label: 'Credit' },
    { value: 'INSTANT', label: 'Instant' },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl w-full">
        <DialogHeader>
          <DialogTitle>Create Purchase Invoice</DialogTitle>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto flex flex-col gap-6">
          <form className="w-full flex flex-col gap-4" onSubmit={e => { e.preventDefault(); handleSubmit(); }}>
            <div className="grid grid-cols-2 gap-x-4 justify-items-stretch">
              <div>
                <p className="text-sm font-semibold mb-2">Invoice Date <span className="text-red-400">*</span></p>
                <div className="space-y-2">
                  <Popover open={invoiceDateOpen} onOpenChange={setInvoiceDateOpen} modal={true}>
                    <PopoverTrigger asChild>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !form.invoice_date && "text-muted-foreground"
                        )}
                        type="button"
                      >
                        {form.invoice_date ? (
                          format(form.invoice_date, "PPP")
                        ) : (
                          <span>Invoice date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.invoice_date}
                        onSelect={date => {
                          if (date) {
                            handleChange('invoice_date', date);
                            setInvoiceDateOpen(false);
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold mb-2">Delivery Date <span className="text-red-400">*</span></p>
                <div className="space-y-2">
                  <Popover open={deliveryDateOpen} onOpenChange={setDeliveryDateOpen} modal={true}>
                    <PopoverTrigger asChild>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !form.delivery_date && "text-muted-foreground"
                        )}
                        type="button"
                      >
                        {form.delivery_date ? (
                          format(form.delivery_date, "PPP")
                        ) : (
                          <span>Delivery date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.delivery_date}
                        onSelect={date => {
                          if (date) {
                            handleChange('delivery_date', date);
                            setDeliveryDateOpen(false);
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold mt-4 mb-2">Invoice Number <span className="text-red-400">*</span></p>
                <div className="space-y-2">
                  <Input
                    placeholder="12345678901234567890"
                    value={form.invoice_number}
                    onChange={e => handleChange('invoice_number', e.target.value)}
                  />
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold mb-2 mt-4">Purchase Order {
                      allowSupplierToBeChanged ? '(Optional)' : ''
                  }</p>
                <div className="space-y-2">
                  <Select
                    disabled={!allowSupplierToBeChanged}
                    onValueChange={value => handleChange('po_id', value)}
                    value={form.po_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Purchase Order (Optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {purchaseOrders.map(po => (
                        <SelectItem value={po.id} key={po.id}>{po.orderNumber}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <hr className="my-3" />
            <h2 className="text-xl font-bold tracking-tight">Payment Info</h2>
            <div className="grid grid-cols-2 gap-x-4 justify-items-stretch">
              <div>
                <p className="text-sm font-semibold mb-2">Method of Payment <span className="text-red-400">*</span></p>
                <div className="space-y-2">
                  <Select
                    onValueChange={value => handleChange('payment_mode', value)}
                    value={form.payment_mode}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Method of Payment" />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentModes.map(pm => (
                        <SelectItem value={pm.value} key={pm.value}>{pm.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold mb-2">Payment Type <span className="text-red-400">*</span></p>
                <div className="space-y-2">
                  <Select
                    onValueChange={value => handleChange('payment_type', value)}
                    value={form.payment_type}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Payment Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {
                          supplierValue ?
                              (supplierValue.is_credit_allowed === "true" ?
                                  <>
                                      <SelectItem value="CREDIT">Credit</SelectItem>
                                      <SelectItem value="INSTANT">Instant</SelectItem>
                                  </>
                                  :
                                  <SelectItem value="INSTANT">Instant</SelectItem>
                              )
                              :
                              <>
                                  <SelectItem value="CREDIT">Credit</SelectItem>
                                  <SelectItem value="INSTANT">Instant</SelectItem>
                              </>
                      }
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.payment_mode === 'CHEQUE' && (
                <div className="mt-4 p-3 border rounded bg-muted col-span-2">
                  <h3 className="font-semibold mb-2">Cheque Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Cheque Number</label>
                      <Input
                        placeholder="Enter cheque number"
                        value={chequeDetails.chequeNumber}
                        onChange={e => setChequeDetails(cd => ({ ...cd, chequeNumber: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Cheque Date</label>
                      <Popover open={chequeDateOpen} onOpenChange={setChequeDateOpen} modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !chequeDetails.chequeDate && "text-muted-foreground"
                            )}
                            type="button"
                          >
                            {chequeDetails.chequeDate ? (
                              format(chequeDetails.chequeDate, "PPP")
                            ) : (
                              <span>Cheque date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={chequeDetails.chequeDate}
                            onSelect={date => {
                              if (date) {
                                setChequeDetails(cd => ({ ...cd, chequeDate: date }));
                                setChequeDateOpen(false);
                              }
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Bank Name</label>
                      <Input
                        placeholder="Enter bank name"
                        value={chequeDetails.bankName}
                        onChange={e => setChequeDetails(cd => ({ ...cd, bankName: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr className="my-3" />
            <h2 className="text-xl font-bold tracking-tight">Supplier Info</h2>
            <div className="grid grid-cols-2 gap-x-4 justify-items-stretch items-end">
              <div>
                <p className="text-sm font-semibold mt-2 mb-2">Supplier <span className="text-red-400">*</span></p>
                <AutoComplete
                    options={filteredSupplierData}
                    placeholder="Search for supplier"
                    emptyMessage="No supplier found"
                    disabled={!allowSupplierToBeChanged}
                    input={supplierInput}
                    onInputChanged={(value) => setSupplierInput(value)}
                    onValueChange={(value) => {
                      setSupplierValue(value);
                      setForm(prev => ({ ...prev, supplier_id: value.value }));
                    }}
                    value={supplierValue}
                />

         
              </div>

            </div>
            <div className="mt">
              <AddSupplier /> 
            </div>
            <DialogFooter>
 
              <Button type="submit">Submit</Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseInvoiceDialog; 